/**
 * PATH: apps/quote/src/components/builder/scope-ag-grid/scope-ag-grid-action-columns.ts
 * FILE_ID: QB-AGGRID-ACTION-COLS-001
 * PURPOSE: Build the two SAM-style synthetic columns that frame the
 *          AG-Grid editor on either side: a row-selection checkbox
 *          column (pinned left) and a per-row actions column with
 *          Copy/Delete icons (pinned right).
 * WHY: scope-ag-grid-columns.ts was already approaching the 300-line
 *      cap before MR 7 added the new columns; pulling the two
 *      action-column builders out keeps that module bounded and makes
 *      these affordances trivially unit-testable in isolation.
 * FLOW: pure I→O — no AG-Grid runtime dependency.
 * RELATED:
 *   - QB-AGGRID-COLUMNS-001 (scope-ag-grid-columns.ts) — consumer
 *   - QB-AGGRID-ROWACTIONS-001 (ScopeAgGridRowActions.tsx) — cell renderer
 *   - QB-AGGRID-PAD-ROWS-001 (pad-row-helpers.ts) — isPadRowId guard
 */

import type { QuoteLineItem } from '@/lib/quoteApi'
import type { AgColDef } from './scope-ag-grid-columns'
import { isPadRowId } from './pad-row-helpers'
import ScopeAgGridRowActions from './ScopeAgGridRowActions'

/**
 * SAM-style row-selection checkbox column. Sits pinned-left at the
 * very edge of the grid, before Item #. Pad rows are non-selectable
 * (the checkbox is suppressed via the function form of
 * ``checkboxSelection``); the header checkbox toggles every real row.
 */
export function buildCheckboxColumn(): AgColDef {
  return {
    colId: '__select',
    headerName: '',
    width: 36,
    minWidth: 36,
    maxWidth: 36,
    pinned: 'left',
    editable: false,
    checkboxSelection: ({ data }: { data?: QuoteLineItem | null }) =>
      !!(data?.id && !isPadRowId(data.id)),
    headerCheckboxSelection: true,
    headerCheckboxSelectionFilteredOnly: true,
    suppressMovable: true,
    sortable: false,
    suppressMenu: true,
    cellClass: 'qb-select-cell',
  }
}

/**
 * SAM-style per-row actions column. Sits pinned-right at the very
 * edge of the grid, after every other band. Renders the Copy +
 * Delete icons via ``ScopeAgGridRowActions``; the icons are kept faint
 * by CSS and brighten on row hover so the chrome stays clean.
 */
export function buildActionsColumn(): AgColDef {
  return {
    colId: '__actions',
    headerName: '',
    width: 64,
    minWidth: 64,
    maxWidth: 96,
    pinned: 'right',
    editable: false,
    sortable: false,
    suppressMovable: true,
    suppressMenu: true,
    cellRenderer: ScopeAgGridRowActions,
    cellClass: 'qb-actions-cell',
  }
}
